import React from "react";
import { Check } from "@phosphor-icons/react";
import { describeFactorProvenance } from "../shared";

/**
 * Iter 19.63.80 / 19.63.81 — Faktor-Provenienz-Badge.
 *
 * Zeigt pro Vordienst-Zeile als kleines Pill-Badge an, woher der aktuelle
 * Anrechnungs-Faktor kommt. Vier Farb-Klassen (siehe `classifyFactorProvenance`):
 *
 *   "kv-rule"      → smaragd-grün, Label „KV-Regel"      (sicher, KV-Match)
 *   "kv-praktikum" → smaragd-grün, Label „KV-Praktikum"  (sicher, User hat gewählt)
 *   "manual"       → orange,        Label „Manuell"       (Override-Audit)
 *   "default"      → slate-blau,    Label „ANNAHME"       (Faktor wurde zu 100 %
 *                                                          angenommen, Prüfung empfohlen)
 *
 * Iter 19.63.81: Label „Default" → „ANNAHME". Hintergrund: Wenn weder eine
 * KV-Regel matched noch ein Praktikum/manueller Override vorliegt, wurde der
 * Faktor 1,0 schlicht angenommen — dieser Status soll für den Sachbearbeiter
 * sofort als „bitte prüfen" erkennbar sein (zusätzlich zu einer subtilen
 * Slate-Tönung der gesamten Row).
 *
 * Hover-Tooltip liefert die Begründung („Faktor 0,5 laut KV-Regel ‚arbeiter'"
 * etc.). Bei `provenance === "n/a"` wird gar kein Badge gerendert.
 */
const STYLE_BY_PROVENANCE = {
  "kv-rule": {
    label: "KV-Regel",
    cls: "border-emerald-400 bg-emerald-50 text-emerald-800",
  },
  "kv-praktikum": {
    // Iter 19.63.86 — Label wird dynamisch unten überschrieben mit
    // „ANGESTELLTER"/„ARBEITER" basierend auf row.praktikum_anstellungstyp.
    // „KV-Praktikum" ist nur Fallback, wenn der Anstellungstyp fehlt.
    label: "KV-Praktikum",
    cls: "border-emerald-400 bg-emerald-50 text-emerald-800",
  },
  manual: {
    label: "Manuell",
    cls: "border-orange-400 bg-orange-50 text-orange-800",
  },
  default: {
    label: "ANNAHME",
    cls: "border-slate-400 bg-slate-100 text-slate-700",
  },
};

export default function FaktorProvenienzBadge({ provenance, row, faktorRegeln, testIdPrefix }) {
  if (provenance === "n/a" || !STYLE_BY_PROVENANCE[provenance]) return null;

  // Iter 20.07 — kv-rule wird analog zum Kompakt-Chip als stummes
  // Häkchen-Icon gerendert (kein Text, kein hover-Tooltip). Text-Badges
  // bleiben nur für die Zustände, die aktive User-Aufmerksamkeit fordern
  // (Manuell, Annahme) oder Zusatz-Info transportieren (KV-Praktikum
  // mit ANGESTELLTER/ARBEITER-Label).
  if (provenance === "kv-rule") {
    return (
      <span
        data-testid={
          testIdPrefix ? `${testIdPrefix}-faktor-provenance-kv-rule` : undefined
        }
        role="img"
        aria-label="Nach KV geregelt"
        className="inline-flex items-center justify-center rounded-sm border border-emerald-400 bg-emerald-50 text-emerald-800 h-[22px] w-[26px]"
      >
        <Check size={13} weight="bold" aria-hidden="true" />
      </span>
    );
  }

  const { label: baseLabel, cls } = STYLE_BY_PROVENANCE[provenance];
  // Iter 19.63.86 — Dynamisches Label für „kv-praktikum": statt des
  // generischen „KV-Praktikum"-Worts zeigen wir die konkrete Wahl des
  // Sachbearbeiters direkt im Badge an. Das ist für den User
  // informationsdichter (er sieht auf einen Blick, dass es ein
  // Angestellter/Arbeiter-Praktikum ist) und ersetzt die zuvor
  // technisch wirkende „KV-Praktikum"-Bezeichnung.
  let label = baseLabel;
  if (provenance === "kv-praktikum") {
    if (row?.praktikum_anstellungstyp === "arbeiter") {
      label = "ARBEITER";
    } else if (row?.praktikum_anstellungstyp === "angestellter") {
      label = "ANGESTELLTER";
    }
  }
  const tooltip = describeFactorProvenance(provenance, row, faktorRegeln);
  return (
    <span
      data-testid={
        testIdPrefix ? `${testIdPrefix}-faktor-provenance-${provenance}` : undefined
      }
      title={tooltip}
      className={[
        "inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm border cursor-help",
        cls,
      ].join(" ")}
    >
      {label}
    </span>
  );
}
